/**
 * Advice under our licence — LICENSEE OVERSIGHT (owner, 1 Oct 2026).
 *
 * Every client advised under AgedCareWise's own AFSL: the direct channel, and clients of firms that
 * are not licensed themselves. Never a client of an adviser firm on its own AFSL. Read-only.
 *
 * The server and database enforce all of it (server/src/rest/compliance/compliance.ts, migration
 * 0030): reviewers only, our-licence records only, a second factor, and every look recorded on the
 * audit trail — opening a record also records the reason typed here.
 *
 * Layout (owner, 1 Oct 2026): one row per client showing the latest version, the rest folded behind
 * a toggle, 25 clients a page — so thousands of clients with many versions each stay scannable. An
 * opened record reads like a document: labelled client details, the fact-find as labelled sections,
 * and the Statement of Advice itself opened as the client saw it. The raw data is still there, folded
 * away, for anyone who needs it.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ClipboardCheck, Loader2, Search, X, Download, Lock, ChevronDown, ChevronRight, FileText, ChevronLeft } from 'lucide-react';
import {
  listAdviceRegister,
  openAdviceRecord,
  explainAdminError,
  type AdviceRegisterEntry,
  type OpenedAdviceRecord,
} from '../../services/adminApi.ts';
import { buildAgedCareDigitalSOAHtml } from '../agedCare/AgedCareSuggestionEngineDigitalSOAAttachment.tsx';
import { buildAdviceBasisSections } from './adviceBasisSections.ts';
import { buildHtml as buildSupportAtHomeSOAHtml } from '../supportAtHome/SupportAtHomeDigitalSOAAttachment.tsx';
import { buildModuleSoaHtml, type ModuleSoaSnapshot } from '../advice/moduleSoa.ts';
import { AdminShell, AdminMessage, StatusDot } from './AdminShell.tsx';

const MODULE_LABELS: Record<string, string> = { aged_care: 'Aged care', support_at_home: 'Support at Home' };
const PAGE_SIZE = 25;

const fmtDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

/** "completerFirstName" -> "Completer first name", for any identity field without a label below. */
const prettify = (key: string): string => {
  const words = key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const IDENTITY_LABELS: Record<string, string> = {
  fullName: 'Name',
  partnerFullName: 'Partner',
  dateOfBirth: 'Date of birth',
  email: 'Email',
  phone: 'Phone',
};

/** The client block, as people read it: name, partner, birth date, contact, then who filled it in. */
function identityRows(identity: Record<string, unknown>): [string, string][] {
  const text = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
  const rows: [string, string][] = [];
  for (const key of ['fullName', 'partnerFullName', 'dateOfBirth', 'email', 'phone']) {
    const v = text(identity[key]);
    if (v) rows.push([IDENTITY_LABELS[key]!, key === 'dateOfBirth' ? fmtDate(v) : v]);
  }
  const completedBy = [text(identity.completerFirstName), text(identity.completerLastName)].filter(Boolean).join(' ');
  if (completedBy) rows.push(['Completed by', completedBy]);
  const handled = new Set(['fullName', 'partnerFullName', 'dateOfBirth', 'email', 'phone', 'completerFirstName', 'completerLastName']);
  for (const [k, v] of Object.entries(identity)) {
    if (!handled.has(k) && text(v)) rows.push([prettify(k), text(v)]);
  }
  return rows;
}

/** The stored document is the saved scenario; its printable snapshot is what the client was given. */
function documentHtml(module: string, doc: unknown): string | null {
  const snapshot = (doc as { soaSnapshot?: unknown } | null)?.soaSnapshot;
  if (!snapshot) return null;
  try {
    if (['retirement_village', 'downsizer', 'granny_flat', 'cashflow_pension'].includes(module)) {
      return buildModuleSoaHtml(snapshot as ModuleSoaSnapshot);
    }
    return module === 'support_at_home'
      ? buildSupportAtHomeSOAHtml(snapshot as Parameters<typeof buildSupportAtHomeSOAHtml>[0])
      : buildAgedCareDigitalSOAHtml(snapshot as Parameters<typeof buildAgedCareDigitalSOAHtml>[0]);
  } catch {
    return null;
  }
}

interface ClientGroup {
  profileId: string;
  clientName: string;
  partnerName: string | null;
  channel: 'direct' | 'firm';
  firmName: string | null;
  fileDeleted: boolean;
  /** Newest first. */
  soas: AdviceRegisterEntry[];
  latestAt: number;
}

interface ComplianceConsoleProps {
  onBack: () => void;
}

const ComplianceConsole = ({ onBack }: ComplianceConsoleProps) => {
  const [records, setRecords] = useState<AdviceRegisterEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [opening, setOpening] = useState<AdviceRegisterEntry | null>(null);
  const [reason, setReason] = useState('');
  const [openBusy, setOpenBusy] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);
  const [opened, setOpened] = useState<OpenedAdviceRecord | null>(null);

  useEffect(() => {
    let live = true;
    listAdviceRegister()
      .then((r) => { if (live) setRecords(r); })
      .catch((err) => { if (live) setError(explainAdminError(err, 'Could not load the advice register.')); });
    return () => { live = false; };
  }, []);

  const groups = useMemo<ClientGroup[]>(() => {
    const byFile = new Map<string, ClientGroup>();
    for (const r of records ?? []) {
      const g = byFile.get(r.profileId) ?? {
        profileId: r.profileId,
        clientName: r.clientName,
        partnerName: r.partnerName,
        channel: r.channel,
        firmName: r.firmName,
        fileDeleted: r.fileDeleted,
        soas: [],
        latestAt: 0,
      };
      g.soas.push(r);
      g.latestAt = Math.max(g.latestAt, new Date(r.publishedAt ?? r.createdAt).getTime());
      byFile.set(r.profileId, g);
    }
    const q = query.trim().toLowerCase();
    return [...byFile.values()]
      .map((g) => ({ ...g, soas: [...g.soas].sort((a, b) => b.version - a.version || b.createdAt.localeCompare(a.createdAt)) }))
      .filter((g) => !q || [g.clientName, g.partnerName, g.firmName ?? 'direct'].some((v) => v?.toLowerCase().includes(q)))
      .sort((a, b) => b.latestAt - a.latestAt);
  }, [records, query]);

  useEffect(() => setPage(0), [query]);
  const pageCount = Math.max(1, Math.ceil(groups.length / PAGE_SIZE));
  const shown = groups.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const startOpen = (r: AdviceRegisterEntry) => {
    setOpening(r);
    setReason('');
    setOpenError(null);
    setOpened(null);
  };

  const closeRecord = () => {
    setOpening(null);
    setOpened(null);
    setOpenError(null);
  };

  const confirmOpen = async () => {
    if (!opening || reason.trim().length < 3) return;
    setOpenBusy(true);
    setOpenError(null);
    try {
      setOpened(await openAdviceRecord(opening.soaId, reason.trim()));
    } catch (err) {
      setOpenError(explainAdminError(err, 'Could not open this record.'));
    } finally {
      setOpenBusy(false);
    }
  };

  const html = opened ? documentHtml(opened.module, opened.document) : null;

  const readDocument = () => {
    if (!html) return;
    const tab = window.open('', '_blank');
    if (!tab) return;
    tab.document.open();
    tab.document.write(html);
    tab.document.close();
  };

  const download = () => {
    if (!opened || !opening) return;
    const blob = new Blob([JSON.stringify(opened, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `advice-record-${opening.clientName.replace(/\W+/g, '-').toLowerCase()}-v${opened.version}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Every screen, every answer — see adviceBasisSections.ts for why not the SOA's own appendix.
  const factSections = opened ? buildAdviceBasisSections(opened.basis.factFind) : [];

  const subtitle =
    records === null
      ? 'Loading…'
      : `${groups.length} client${groups.length === 1 ? '' : 's'} · ${records.length} Statement${records.length === 1 ? '' : 's'} of Advice · read-only`;

  const soaLine = (r: AdviceRegisterEntry, primary: boolean) => (
    <div key={r.soaId} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 sm:grid-cols-[7.5rem_minmax(0,1fr)_auto_auto]">
      <span className={`${primary ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
        {MODULE_LABELS[r.module] ?? r.module} · v{r.version}
      </span>
      <span className="hidden sm:block">
        <StatusDot
          color={r.status === 'published' ? '#059669' : '#94a3b8'}
          label={r.status === 'published' ? `Published ${fmtDate(r.publishedAt)}` : `Draft ${fmtDate(r.createdAt)}`}
        />
      </span>
      <span className="hidden text-xs text-slate-500 sm:block">Kept until {fmtDate(r.keepUntil)}</span>
      <button
        type="button"
        onClick={() => startOpen(r)}
        className="justify-self-end rounded-lg bg-[#1a6fd4] px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-[#155bb0] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1a6fd4]/40"
      >
        Open record
      </button>
    </div>
  );

  return (
    <AdminShell title="Advice under our licence" subtitle={subtitle} icon={<ClipboardCheck size={22} />} onBack={onBack}>
      <p className="mb-6 flex max-w-3xl items-start gap-2 text-sm leading-relaxed text-slate-600">
        <Lock size={15} className="mt-0.5 shrink-0 text-[#1a6fd4]" />
        Direct clients, and clients of firms that are not licensed themselves. Clients of advisers on their own AFSL are never
        shown. Opening this list, and every record, is recorded on the audit trail.
      </p>

      {error && <AdminMessage tone="error">{error}</AdminMessage>}

      {records === null && !error ? (
        <div className="flex items-center justify-center gap-2 py-20 text-slate-400">
          <Loader2 size={18} className="animate-spin" /> Loading…
        </div>
      ) : records && (
        <>
          <label className="mb-5 flex max-w-md items-center gap-2 rounded-lg border border-slate-300 px-3 py-2.5 focus-within:border-[#1a6fd4] focus-within:ring-2 focus-within:ring-[#1a6fd4]/20">
            <Search size={16} className="text-slate-400" />
            <span className="sr-only">Search</span>
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by client or firm" className="w-full bg-transparent text-sm outline-none" />
          </label>

          {groups.length === 0 ? (
            <p className="rounded-xl border border-dashed border-slate-300 py-14 text-center text-sm text-slate-500">
              {records.length === 0 ? 'No advice has been given under our licence yet.' : 'No client matches that search.'}
            </p>
          ) : (
            <>
              <ul className="divide-y divide-slate-200 rounded-xl border border-slate-200">
                {shown.map((g) => {
                  const [latest, ...older] = g.soas;
                  const isOpen = expanded.has(g.profileId);
                  return (
                    <li key={g.profileId} className="px-5 py-4">
                      <div className="grid gap-3 text-sm md:grid-cols-[minmax(0,15rem)_minmax(0,1fr)]">
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900">
                            {g.clientName}
                            {g.partnerName && <span className="font-normal text-slate-500"> & {g.partnerName}</span>}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                            <span>{g.channel === 'direct' ? 'Direct client' : `Via ${g.firmName ?? 'a firm'}`}</span>
                            {g.fileDeleted && <StatusDot color="#d97706" label="File deleted, record kept" />}
                          </div>
                        </div>
                        <div className="space-y-2">
                          {latest && soaLine(latest, true)}
                          {older.length > 0 && (
                            <button
                              type="button"
                              onClick={() => toggle(g.profileId)}
                              aria-expanded={isOpen}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-[#1a6fd4] hover:underline"
                            >
                              {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              {isOpen ? 'Hide earlier versions' : `${older.length} earlier version${older.length === 1 ? '' : 's'}`}
                            </button>
                          )}
                          {isOpen && <div className="space-y-2 border-l-2 border-slate-100 pl-3">{older.map((r) => soaLine(r, false))}</div>}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>

              {pageCount > 1 && (
                <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
                  <span>
                    Clients {page * PAGE_SIZE + 1}–{Math.min(groups.length, (page + 1) * PAGE_SIZE)} of {groups.length}
                  </span>
                  <span className="flex gap-2">
                    <button type="button" disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 font-medium disabled:opacity-40">
                      <ChevronLeft size={15} /> Previous
                    </button>
                    <button type="button" disabled={page >= pageCount - 1} onClick={() => setPage((p) => p + 1)} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 font-medium disabled:opacity-40">
                      Next <ChevronRight size={15} />
                    </button>
                  </span>
                </div>
              )}
            </>
          )}
        </>
      )}

      {opening && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/60 p-4 backdrop-blur-[2px] sm:p-10" role="dialog" aria-modal="true" aria-labelledby="record-title">
          <div className="w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-900/10">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-4">
              <div>
                <div id="record-title" className="text-lg font-semibold text-slate-900">{opening.clientName}</div>
                <div className="text-sm text-slate-500">
                  {MODULE_LABELS[opening.module] ?? opening.module} Statement of Advice · version {opening.version}
                </div>
              </div>
              <button type="button" onClick={closeRecord} aria-label="Close" className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700">
                <X size={18} />
              </button>
            </div>

            {!opened ? (
              <div className="space-y-3 px-6 py-6">
                <label className="block text-sm font-semibold text-slate-800" htmlFor="compliance-reason">
                  Why are you opening this record?
                </label>
                <input
                  id="compliance-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void confirmOpen(); }}
                  placeholder="e.g. Quarterly advice review, or a complaint reference"
                  maxLength={300}
                  autoFocus
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-[#1a6fd4] focus:ring-2 focus:ring-[#1a6fd4]/20"
                />
                <p className="text-xs text-slate-500">Your reason is saved on the audit trail with your name and the time.</p>
                {openError && <AdminMessage tone="error">{openError}</AdminMessage>}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={confirmOpen}
                    disabled={openBusy || reason.trim().length < 3}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-[#1a6fd4] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#155bb0] disabled:bg-slate-300"
                  >
                    {openBusy && <Loader2 size={14} className="animate-spin" />} Open record
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-6 px-6 py-6 text-sm">
                <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
                  {[
                    ['Status', opened.status === 'published' ? `Published ${fmtDate(opened.publishedAt)}` : `Draft, ${fmtDate(opened.createdAt)}`],
                    ['Licensee', opened.licenseeName ? `${opened.licenseeName}${opened.afslNumber ? ` · AFSL ${opened.afslNumber}` : ''}` : 'Not recorded'],
                    ['Kept until', fmtDate(opened.keepUntil)],
                    ['Details sealed', fmtDate(opened.basisSealedAt)],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <dt className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">{k}</dt>
                      <dd className="mt-1 text-slate-900">{v}</dd>
                    </div>
                  ))}
                </dl>

                <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 px-4 py-3">
                  <FileText size={18} className="shrink-0 text-[#1a6fd4]" />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900">The Statement of Advice</div>
                    <div className="text-xs text-slate-500">
                      {html ? 'Opens in a new tab, as the client saw it.' : 'This version was saved without its printable document; the record below is complete.'}
                    </div>
                  </div>
                  {html && (
                    <button type="button" onClick={readDocument} className="rounded-lg bg-[#1a6fd4] px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-[#155bb0]">
                      Read the Statement of Advice
                    </button>
                  )}
                </div>

                <section>
                  <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">Client, as at the advice</h3>
                  <dl className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
                    {identityRows(opened.basis.identity).map(([k, v]) => (
                      <div key={k} className="grid grid-cols-[8.5rem_minmax(0,1fr)] gap-3">
                        <dt className="text-slate-500">{k}</dt>
                        <dd className="break-words text-slate-900">{v}</dd>
                      </div>
                    ))}
                  </dl>
                </section>

                <section>
                  <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">What the advice was based on</h3>
                  {factSections.length === 0 ? (
                    <p className="text-slate-500">No fact-find details were recorded.</p>
                  ) : (
                    <div className="space-y-2">
                      {factSections.map((section) => (
                        <details key={section.title} className="rounded-lg border border-slate-200">
                          <summary className="cursor-pointer px-4 py-2.5 font-medium text-slate-800">{section.title}</summary>
                          <dl className="divide-y divide-slate-100 border-t border-slate-200">
                            {section.rows.map((row, i) => (
                              <div key={`${row.label}-${i}`} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4 px-4 py-2">
                                <dt className="text-slate-500">{row.label}</dt>
                                <dd className="break-words text-slate-900">{row.value}</dd>
                              </div>
                            ))}
                          </dl>
                        </details>
                      ))}
                    </div>
                  )}
                </section>

                <details className="rounded-lg border border-dashed border-slate-300">
                  <summary className="cursor-pointer px-4 py-2.5 text-slate-600">Technical record (raw data, for IT or a regulator's request)</summary>
                  <div className="space-y-3 border-t border-slate-200 px-4 py-3">
                    <pre className="max-h-64 overflow-auto font-mono text-xs leading-relaxed text-slate-700">{JSON.stringify(opened.basis.factFind, null, 2)}</pre>
                    <pre className="max-h-64 overflow-auto font-mono text-xs leading-relaxed text-slate-700">{JSON.stringify(opened.document, null, 2)}</pre>
                    <button type="button" onClick={download} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:text-slate-900">
                      <Download size={15} /> Download the full record
                    </button>
                  </div>
                </details>
              </div>
            )}
          </div>
        </div>
      )}
    </AdminShell>
  );
};

export default ComplianceConsole;
